/** * カレンダーコンポーネント */
<template>
  <div class="schedule-calendar">
    <loading-mask v-bind:isLoading="isCalenderLoading">
      <div class="schedule-calendar-body">
        <!-- カレンダー -->
        <vue-cal
          ref="vuecal"
          class="vuecal"
          active-view="month"
          v-bind:time-from="7 * 60"
          v-bind:time-to="21 * 60"
          v-bind:disable-views="['years', 'year', 'week', 'days', 'day']"
          v-bind:hideViewSelector="true"
          v-bind:hideTitleBar="true"
          v-bind:show-all-day-events="true"
          v-bind:events-count-on-year-view="true"
          v-bind:events="events"
          v-bind:locale="locale"
          v-bind:selected-date="selectedDate"
          v-on:cell-click="$_onEventClick"
          v-on:ready="$_onReady"
          v-on:view-change="$_onMonthChange"
        >
          <!-- セル内表示テンプレート -->
          <template v-slot:cell-content="{ cell, events }">
            <!-- 日付・祝日表示行 -->
            <b-row
              no-gutters
              class="cell-date-row"
              v-bind:class="{
                'holiday-row':
                  holidays[cell.startDate.format(DATE_FORMAT_HYPHEN)],
              }"
            >
              <span class="vuecal__cell-date">
                {{ cell.startDate.getDate() }}
              </span>
              <span class="national-holidays">
                {{ holidays[cell.startDate.format(DATE_FORMAT_HYPHEN)] || '' }}
              </span>
            </b-row>
            <!-- 件数表示行 -->
            <b-row no-gutters class="cell-count-row">
              <span v-if="events.length" class="events-count">
                {{ $tc('appScheduleCalendar.count', events.length) }}
              </span>
            </b-row>
          </template>
        </vue-cal>
      </div>
    </loading-mask>
  </div>
</template>

<script>
import vueCal from 'vue-cal'
import 'vue-cal/dist/i18n/ja.es.js'
import 'vue-cal/dist/vuecal.css'
import { RestApiClient } from 'ifield-core-components'
import { getActiveLocale } from '@/i18n'
import Schedule from '@/model/Schedule.js'
import { toLocalTz, toServerTz } from '@/js/TimezoneHelper'
import {
  DATE_FORMAT_HYPHEN,
  TIME_FORMAT_MINUTE,
  MINIMUM_TIME,
  MAXIMUM_TIME,
} from '@/constants'
import moment from 'moment'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'

export default {
  name: 'AppScheduleCalendar',
  components: {
    vueCal,
  },
  props: {
    /**
     * カレンダー開始曜日（1:日曜 2:月曜）
     */
    startDayOfWeek: {
      type: Number,
      default: 2,
    },
    /**
     * 「担当外を含む」指定状態
     */
    includeNonAssignedSchedule: {
      type: Boolean,
      default: false,
    },
    /**
     * スケジュール完了フラグ
     */
    completeFlg: {
      type: Boolean,
      default: null,
    },
    /**
     * スケジュール表示対象ユーザー
     */
    user: {
      type: Object,
      default: null,
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError, DATE_FORMAT_HYPHEN }
  },
  data: function () {
    return {
      // カレントのカレンダービュー情報
      currentView: {},
      // 選択中の日付
      selectedDate: moment().format(DATE_FORMAT_HYPHEN),
      // 休日データ
      holidays: [],
      // 表示スケジュール
      events: [],
      // カレンダーローディングフラグ
      isCalenderLoading: false,
    }
  },
  computed: {
    /**
     * ロケール取得
     * @type {String}
     */
    locale: function () {
      return getActiveLocale()
    },
    /**
     * ログインユーザーが管理者の場合はtrue
     * @type {Boolean}
     */
    isManager: function () {
      return this.$store.state.login.isManager
    },
    /**
     * API用カレントビューの情報
     * @type {Object.<String>}
     */
    currentViewInfo: function () {
      if (this.currentView.startDate) {
        // startDateが入っていれば他の情報も入っていることにする
        return {
          start: moment(this.currentView.startDate).format(DATE_FORMAT_HYPHEN),
          end: moment(this.currentView.endDate).format(DATE_FORMAT_HYPHEN),
          year: moment(this.currentView.startDate).format('YYYY'),
          month: moment(this.currentView.startDate).format('MM'),
        }
      } else {
        // 現在で作成
        return {
          start: moment().startOf('month').format(DATE_FORMAT_HYPHEN),
          end: moment().endOf('month').format(DATE_FORMAT_HYPHEN),
          year: moment().startOf('month').format('YYYY'),
          month: moment().endOf('month').format('MM'),
        }
      }
    },
    /**
     * カレントの年月名
     * @type {String}
     */
    displayCurrentMonth: function () {
      return moment(this.currentView.startDate).format(
        this.$t('appScheduleCalendar.format.month')
      )
    },
    /**
     * 休日取得用URL
     * @type {URL}
     */
    holidayURL: function () {
      const url = RestApiClient.createApiUrl('holiday')
      const params = url.searchParams
      params.append('year', this.currentViewInfo.year)
      params.append('month', this.currentViewInfo.month)

      return url
    },
    /**
     * スケジュール取得用URL
     * @type {URL}
     */
    scheduleURL: function () {
      const url = RestApiClient.createApiUrl('schedule')
      const params = url.searchParams
      params.append(
        'from',
        toServerTz(`${this.currentViewInfo.start}T${MINIMUM_TIME}`)
      )
      params.append(
        'to',
        toServerTz(`${this.currentViewInfo.end}T${MAXIMUM_TIME}`)
      )
      if (this.user) {
        params.append('extend_user_id', this.user.id)
      }
      if (typeof this.completeFlg === 'boolean') {
        params.append('complete_flg', this.completeFlg)
      }
      params.append('others_schedules', this.includeNonAssignedSchedule)

      return url
    },
    /**
     * カレンダー開始日は日曜日か？
     * @type {Boolean}
     */
    isStartWeekOnSunday: function () {
      // 日曜日の場合のみtrue、それ以外は無視するfalse（月曜日始まり）
      return this.startDayOfWeek === 1
    },
    /**
     * 最後に保存されたスケジュール
     * @type {Schedule}
     */
    scheduleLastSaved: function () {
      return new Schedule(this.$store.state.app.schedule.lastSaved)
    },
    /**
     * 最終保存スケジュールに対象ユーザーが含まれる場合はtrue
     * @type {Boolean}
     */
    isUserContainedInScheduleLastSaved: function () {
      if (!this.isManager) {
        // 承認権限が無い場合は自分のスケジュールしか登録できないので必ず対象になっている
        return true
      }
      const targetUserId = this.user
        ? this.user.id
        : this.$store.getters.loginState.userId
      return this.scheduleLastSaved.isTargetUser(targetUserId)
    },
    /**
     * カレンダー表示内に最終保存スケジュールが含まれている場合はtrue
     * @type {Boolean}
     */
    isCalendarOverlapsScheduleLastSaved: function () {
      if (!this.currentView.firstCellDate) {
        return false
      }
      return !(
        this.scheduleLastSaved.endAt.isBefore(
          moment(this.currentView.firstCellDate)
        ) ||
        this.scheduleLastSaved.startAt.isAfter(
          moment(this.currentView.lastCellDate)
        )
      )
    },
  },
  watch: {
    startDayOfWeek: function () {
      // カレンダーを再作成する
      this.$_createSchedule()
    },
    scheduleLastSaved: function () {
      // カレンダー表示対象だったらリフレッシュする
      this.isUserContainedInScheduleLastSaved &&
        this.isCalendarOverlapsScheduleLastSaved &&
        this.refreshCalendar()
    },
    completeFlg: function () {
      this.refreshCalendar()
    },
    includeNonAssignedSchedule: function () {
      this.refreshCalendar()
    },
    user: function () {
      this.refreshCalendar()
    },
  },
  mounted: function () {
    this.$log.debug('schedule calendar mounted')
    this.$_createSchedule()
  },
  methods: {
    /**
     * カレンダーを更新する
     * @public
     */
    refreshCalendar: async function () {
      this.$_showCalenderLoading()
      await this.$_createMonthCalender()
      this.$_hideCalenderLoading()
    },

    /**
     * 前月への移動処理
     * @public
     */
    onPreviousMonth: function () {
      this.$_showCalenderLoading()

      try {
        this.$refs['vuecal'].previous()
      } catch (e) {
        this.$log.error(`not change previous month:${e.message}`)
      }
    },

    /**
     * 次月への移動処理
     * @public
     */
    onNextMonth: function () {
      this.$_showCalenderLoading()

      try {
        this.$refs['vuecal'].next()
      } catch (e) {
        this.$log.error(`not change next month:${e.message}`)
      }
    },

    /**
     * イベントをクリックした際の処理
     * @param {Date} event クリックしたイベントのDate
     */
    $_onEventClick: async function (event) {
      this.selectedDate = moment(event).format(DATE_FORMAT_HYPHEN)
      /**
       * カレンダーの特定日付を選択した時のイベント
       * @param {String} targetDate 選択した日付
       */
      this.$emit('schedule-date-selectend', this.selectedDate)
    },

    /**
     * カレンダー開始曜日の変更
     * @param {Date} 再描画用表示日
     */
    $_changeStartOnWeek: function (date) {
      // startWeekOnSundayはプロパティだが、バインディングではカレンダーが再描画されないので直接変更している
      // コンソールにエラーが出るが、既知のエラーとして無視してよい
      this.$refs['vuecal'].startWeekOnSunday = this.isStartWeekOnSunday
      // カレンダ再作成のため、以下メソッドを呼ぶ
      this.$refs['vuecal'].switchView('month', date)
    },

    /**
     * スケジュール初期設定
     */
    $_createSchedule: function () {
      this.$log.debug('create schedule start')
      this.$_changeStartOnWeek(moment(this.selectedDate).toDate())
      this.refreshCalendar()
    },

    /**
     * カレンダー準備完了時のイベント処理
     * @param {Object} event ビュー情報
     */
    $_onReady: function (event) {
      this.currentView = event
      /**
       * カレンダー準備完了時のイベント
       * @param {String} displayCurrentMonth 表示中月
       */
      this.$emit('calendar-onready', this.displayCurrentMonth)
    },

    /**
     * 表示月が変わった際のイベント処理
     * @param {Object} event 変更後ビュー情報
     */
    $_onMonthChange: function (event) {
      this.currentView = event
      this.refreshCalendar()
      /**
       * カレンダーの表示月変更時のイベント
       * @param {String} displayCurrentMonth 表示中月
       */
      this.$emit('change-view-month', this.displayCurrentMonth)
    },

    /**
     * 月間のカレンダーを作成する
     */
    $_createMonthCalender: async function () {
      let holidays, schedules
      try {
        ;[holidays, schedules] = await Promise.all([
          await RestApiClient.executeGetApi(this.holidayURL).then(
            ({ data }) => data
          ),
          await RestApiClient.executeGetApi(this.scheduleURL).then(({ data }) =>
            data.map((d) => ({
              ...d,
              // カレンダー表示に必要な開始・終了日時のみタイムゾーン変換
              start_at: d.allday_flg ? d.start_at : toLocalTz(d.start_at),
              end_at: d.allday_flg ? d.end_at : toLocalTz(d.end_at),
            }))
          ),
        ])
      } catch (e) {
        this.$log.error(`not create calender : ${e.message}`)
        this.handleApiError(e)
        return
      }

      this.$_setHolidayData(holidays)
      this.$_setScheduleData(schedules)
    },

    /**
     * 月間スケジュールのデータを作成する
     * @param {Object[]} data APIのレスポンスデータ
     */
    $_setScheduleData: function (data) {
      this.events = data.map((event) => {
        const start = moment(event.start_at)
        const end = event.allday_flg
          ? moment(event.end_at).endOf('day')
          : moment(event.end_at)
        return {
          start: start.format(`${DATE_FORMAT_HYPHEN} ${TIME_FORMAT_MINUTE}`),
          end: end.format(`${DATE_FORMAT_HYPHEN} ${TIME_FORMAT_MINUTE}`),
          title: event.title,
          content: event.detail,
          contentFull: event.detail, // Custom attribute.
        }
      })
    },

    /**
     * カレンダーの休日の色を赤に設定する
     * @param {Object[]} data APIのレスポンスデータ
     */
    $_setHolidayData: function (data) {
      this.holidays = Object.fromEntries(
        data.map((d) => [d.holiday, d.holiday_name])
      )
    },

    /**
     * カレンダーのローディングを表示する
     */
    $_showCalenderLoading: function () {
      this.isCalenderLoading = true
    },

    /**
     * カレンダーのローディングを非表示にする
     */
    $_hideCalenderLoading: function () {
      this.isCalenderLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.schedule-calendar-body {
  width: 100%;
  height: 100%;
  background-color: $entity-color-white;
  padding: 0 20px;
}

.vuecal {
  box-shadow: none;
  ::v-deep .vuecal__weekdays-headings {
    padding-right: 0;
    font-size: $font-size-small;
    border: none;

    .vuecal__heading {
      height: 1.5rem;
    }
  }

  ::v-deep .vuecal__cell {
    padding: 0.3px;
    height: 16.6667%;

    &::before {
      border: none;
    }
  }

  ::v-deep .vuecal__cell-content {
    border: 1px solid #ebebeb;
  }

  ::v-deep .cell-date-row {
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: nowrap;
    column-gap: 4px;
    height: 1.25rem;
    width: 100%;
    padding: 0 4px;
    line-height: 1;
    font-size: $font-size-small;

    &.holiday-row {
      color: #ff0000;
    }
  }

  ::v-deep .cell-count-row {
    height: calc(100% - 1.25rem);
  }

  ::v-deep .vuecal__cell-date {
    text-align: left;
  }

  ::v-deep .vuecal__cell--out-of-scope {
    color: inherit;

    .vuecal__cell-content {
      background-color: #eeeeee;
    }
  }

  ::v-deep .vuecal__cell--day6 {
    color: #0000ff;
  }

  ::v-deep .vuecal__cell--day7 {
    color: #ff0000;
  }

  ::v-deep .events-count {
    position: absolute;
    bottom: 0;
    width: 100%;
    background-color: #2a8e5c;
    border-radius: 0px;
    font-size: 0.625rem;
    color: $entity-color-white;
    vertical-align: middle;
  }

  ::v-deep .national-holidays {
    font-size: 0.5rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left;
  }

  ::v-deep .vuecal__cell--selected {
    .vuecal__cell-content {
      background-color: #feefdf;
      border: 1px solid #fcce62;
    }
  }
}
</style>
